import { Box, Checkbox, Typography, useTheme } from '@mui/material';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import React, { FC, useContext, useEffect, useState } from 'react';
import { green, orange, red } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';

interface Props {
  primaryColor: string;
  secondaryColor: string;
  customFont: string;
  companyName: string;
  negativeText: string;
  positiveText: string;
  neutralText: string;
  title: string;
  onChange: (value: string) => void;
  question?: string;
}

const MoodBarometerIntro: FC<Props> = (props) => {
  const {
    primaryColor,
    secondaryColor,
    customFont,
    companyName,
    title,
    negativeText,
    positiveText,
    neutralText,
    question,
    onChange,
  } = props;

  const theme = useTheme();
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation();
  const t = localContext ? localContext.t : globalT;
  const [selectedMood, setSelectedMood] = useState<string>('');

  useEffect(() => {
    onChange(selectedMood);
  }, [selectedMood]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <Typography
        variant="h4"
        sx={{
          color: primaryColor,
          marginBottom: theme.spacing(8),
          fontWeight: 'bold',
          fontFamily: customFont,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          color: secondaryColor,
          mb: theme.spacing(1),
          mt: theme.spacing(3),
          fontFamily: customFont,
        }}
      >
        {question}
      </Typography>
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: theme.spacing(2),
            mt: theme.spacing(2),
            pl: theme.spacing(4),
            cursor: 'pointer',
          }}
          onClick={() => setSelectedMood('positive')}
        >
          <Checkbox
            checked={selectedMood === 'positive'}
            onChange={() => setSelectedMood('positive')}
          />
          <SentimentSatisfiedAltIcon
            fontSize={'large'}
            sx={{ color: green[500] }}
          />
          <Typography
            variant="h5"
            sx={{
              color: secondaryColor,
              fontFamily: customFont,
              fontSize: '1.2rem',
            }}
          >
            {positiveText}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: theme.spacing(2),
            mt: theme.spacing(2),
            pl: theme.spacing(4),
            cursor: 'pointer',
          }}
          onClick={() => setSelectedMood('neutral')}
        >
          <Checkbox
            checked={selectedMood === 'neutral'}
            onChange={() => setSelectedMood('neutral')}
          />
          <SentimentSatisfiedIcon
            fontSize={'large'}
            sx={{ color: orange[500] }}
          />
          <Typography
            variant="h5"
            sx={{
              color: secondaryColor,
              fontFamily: customFont,
              fontSize: '1.2rem',
            }}
          >
            {neutralText}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: theme.spacing(2),
            mt: theme.spacing(2),
            pl: theme.spacing(4),
            cursor: 'pointer',
          }}
          onClick={() => setSelectedMood('negative')}
        >
          <Checkbox
            checked={selectedMood === 'negative'}
            onChange={() => setSelectedMood('negative')}
          />
          <SentimentVeryDissatisfiedIcon
            fontSize={'large'}
            sx={{ color: red[500] }}
          />
          <Typography
            variant="h5"
            sx={{
              color: secondaryColor,
              fontFamily: customFont,
              fontSize: '1.2rem',
            }}
          >
            {negativeText}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MoodBarometerIntro;
